function pluralize(count: number, unit: string): string {
  return count === 1 ? `1 ${unit}` : `${count} ${unit}s`;
}

function formatDays(days: number): string {
  return pluralize(Math.max(1, days), 'day');
}

/** Human stay length from timestamps, falling back to inclusive calendar days. */
export function formatStayDuration(
  startedAt?: string,
  endedAt?: string,
  fallbackDays = 1,
  nowMs = Date.now(),
): string {
  if (!startedAt) return formatDays(fallbackDays);

  const start = Date.parse(startedAt);
  if (Number.isNaN(start)) return formatDays(fallbackDays);

  const parsedEnd = endedAt ? Date.parse(endedAt) : Number.NaN;
  const end = Number.isNaN(parsedEnd) ? nowMs : parsedEnd;
  const totalMinutes = Math.max(0, Math.round((end - start) / 60_000));

  if (totalMinutes < 1) return 'less than a minute';
  if (totalMinutes < 60) return pluralize(totalMinutes, 'minute');

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours < 24) {
    if (minutes === 0) return pluralize(hours, 'hour');
    return `${pluralize(hours, 'hour')} ${pluralize(minutes, 'minute')}`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  if (remainingHours === 0) return pluralize(days, 'day');
  return `${pluralize(days, 'day')} ${pluralize(remainingHours, 'hour')}`;
}

export type StayTone = 'fresh' | 'lingering' | 'stale';

/** Urgency of a Now task by days in Now this stay: fresh <3, lingering 3–4, stale ≥5. */
export function stayTone(days: number): StayTone {
  if (days >= 5) return 'stale';
  if (days >= 3) return 'lingering';
  return 'fresh';
}

/** Gentle nudge copy for tasks that have lingered in Now, or null while still fresh. */
export function stayNudge(days: number): string | null {
  if (days >= 7) return "A week in the spotlight. Let's close it.";
  if (days >= 5) return "It's getting comfortable. Your move.";
  if (days >= 3) return 'Still hanging around. Today?';
  return null;
}

/** Short stay label: "Added 2h ago" on day 1 (from `addedAt`), otherwise "Day N". */
export function stayLabel(
  item: { currentStayDays: number; addedAt?: string },
  nowMs = Date.now(),
): string {
  const days = Math.max(1, item.currentStayDays);
  if (days > 1) return `Day ${days}`;

  const added = item.addedAt ? Date.parse(item.addedAt) : Number.NaN;
  if (Number.isNaN(added)) return 'Day 1';

  const minutes = Math.max(0, Math.floor((nowMs - added) / 60_000));
  if (minutes < 1) return 'Added just now';
  if (minutes < 60) return `Added ${minutes}m ago`;
  return `Added ${Math.floor(minutes / 60)}h ago`;
}
